/**
 * Sprint 24.5 — InstallStepAuthorize (wizard step 2).
 *
 * The OAuth handoff. Reuses Sprint 7.2's connection-scoped flow: the connection
 * already exists (registered when leaving Confirm), so we open
 * `/oauth/authorize?workspace_id&connection_id&user_id` in a popup. Completion is
 * detected two ways for robustness: the popup's postMessage (`oauth_complete`,
 * the proven Sprint 7.x/22 signal) AND polling `/oauth/status` every 500ms until
 * the connection's token appears (Q9). 10-minute timeout → error + retry.
 *
 * The popup opens on an explicit click so the browser keeps the user-gesture
 * (auto-opening on mount is blocked by popup blockers); once opened we show the
 * brand-coloured waiting moment.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { mcpClient } from "../../../../lib/mcp-client";
import { authFetch } from "../../../../lib/auth";
import { workerOrigin } from "../../../../hooks/use-connections";
import { useAuth } from "../../../../lib/hooks/use-auth";
import type { FirstPartyTemplate } from "../../../../hooks/use-first-party-templates";

type Phase = "idle" | "waiting" | "error";

const POLL_MS = 500;
const TIMEOUT_MS = 10 * 60_000;

export interface InstallStepAuthorizeProps {
  template: FirstPartyTemplate;
  connectionId: string | null;
  onAuthorized: () => void;
  onCancel: () => void;
}

export function InstallStepAuthorize({
  template,
  connectionId,
  onAuthorized,
  onCancel,
}: InstallStepAuthorizeProps) {
  const { user, currentWorkspaceId } = useAuth();
  const [phase, setPhase] = useState<Phase>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const popupRef = useRef<Window | null>(null);
  const doneRef = useRef(false);

  // Poll /oauth/status + listen for the popup's postMessage while waiting.
  useEffect(() => {
    if (phase !== "waiting" || !connectionId) return;
    doneRef.current = false;

    const succeed = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      popupRef.current?.close();
      onAuthorized();
    };

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; connectionId?: string } | undefined;
      if (data?.type === "oauth_complete" && data.connectionId === connectionId) succeed();
      if (data?.type === "oauth_error" && data.connectionId === connectionId) {
        if (!doneRef.current) {
          setPhase("error");
          setErrorMsg("Authorization declined. You can try again.");
        }
      }
    };
    window.addEventListener("message", onMessage);

    const poll = window.setInterval(async () => {
      try {
        const resp = await authFetch(`${workerOrigin}/oauth/status`);
        if (!resp.ok) return;
        const data = (await resp.json()) as {
          tokens?: Record<string, { authorized?: boolean }>;
        };
        if (data.tokens?.[connectionId]?.authorized) succeed();
      } catch {
        /* transient — keep polling */
      }
    }, POLL_MS);

    const timeout = window.setTimeout(() => {
      if (!doneRef.current) {
        setPhase("error");
        setErrorMsg("Timed out waiting for authorization. You can try again.");
      }
    }, TIMEOUT_MS);

    const popupWatch = window.setInterval(() => {
      if (popupRef.current?.closed && !doneRef.current) {
        // Popup closed without completing — fall back to idle so the user can retry.
        window.clearInterval(popupWatch);
        if (!doneRef.current) setPhase((p) => (p === "waiting" ? "idle" : p));
      }
    }, 1000);

    return () => {
      window.removeEventListener("message", onMessage);
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      window.clearInterval(popupWatch);
    };
  }, [phase, connectionId, onAuthorized]);

  function openPopup() {
    if (!connectionId || !currentWorkspaceId || !user) {
      setPhase("error");
      setErrorMsg("Couldn't start authorization — missing session context.");
      return;
    }
    const url = new URL("/oauth/authorize", mcpClient.apiOrigin);
    url.searchParams.set("workspace_id", currentWorkspaceId);
    url.searchParams.set("connection_id", connectionId);
    url.searchParams.set("user_id", user.id);
    const popup = window.open(url.toString(), `oauth_${connectionId}`, "width=600,height=700,popup=yes");
    if (!popup) {
      setPhase("error");
      setErrorMsg("Popup blocked. Allow popups for Kuvio and try again.");
      return;
    }
    popupRef.current = popup;
    setErrorMsg("");
    setPhase("waiting");
  }

  const ring: CSSProperties = {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: template.brand.avatar_bg,
    color: template.brand.avatar_color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    flexShrink: 0,
  };

  return (
    <div style={body}>
      <div style={center}>
        <span style={ring}>
          <i className={`ti ti-${template.brand.icon_name}`} aria-hidden="true" />
        </span>

        {phase === "error" ? (
          <>
            <h3 style={h3}>Authorization didn't finish</h3>
            <p style={sub}>{errorMsg}</p>
          </>
        ) : (
          <>
            <h3 style={h3}>Authorizing with {template.display_name}…</h3>
            <p style={sub}>
              {phase === "waiting"
                ? "A popup opened. Sign in and approve the requested permissions."
                : "We'll open a secure popup to sign in and approve the requested permissions."}
            </p>
          </>
        )}

        {phase === "waiting" && (
          <div style={dots} aria-label="Waiting for authorization">
            <span style={{ ...dot, animationDelay: "0ms" }} />
            <span style={{ ...dot, animationDelay: "150ms" }} />
            <span style={{ ...dot, animationDelay: "300ms" }} />
          </div>
        )}
      </div>

      <div style={footer}>
        <button type="button" style={cancelLink} onClick={onCancel}>
          Cancel
        </button>
        {phase !== "waiting" && (
          <button type="button" style={primaryBtn} onClick={openPopup}>
            {phase === "error" ? "Retry" : `Open ${template.display_name} sign-in →`}
          </button>
        )}
      </div>

      <style>{keyframes}</style>
    </div>
  );
}

const keyframes = `
@keyframes kuvio-oauth-dot {
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
}`;

const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: "24px 28px",
  fontFamily: "var(--font-sans)",
};

const center: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 10,
  padding: "8px 0",
};

const h3: CSSProperties = { fontSize: 16, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const sub: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  margin: 0,
  maxWidth: 320,
  lineHeight: 1.5,
};

const dots: CSSProperties = { display: "flex", gap: 6, marginTop: 4 };
const dot: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "var(--color-text-tertiary)",
  animation: "kuvio-oauth-dot 1.2s infinite ease-in-out",
};

const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 4,
};

const cancelLink: CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
